import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, AlertCircle, ZapOff, ExternalLink, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../../../styles/features/status/StatusPageCategories.css';
import { getStoredJSON, setStoredJSON } from '../../../shared/utils/storage';
import { getUptimeColorValue, formatUptimePercent, getStatusLabel, getDisplayStatus } from '../../../shared/utils/status';
import { UsageMeter } from '../../../shared/components';

function StatusPageCategories({ categories, endpoints }) {
  const [collapsedState, setCollapsedState] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    setCollapsedState(getStoredJSON('categoryCollapseState', {}));
  }, []);

  const toggleCategory = (categoryId) => {
    const isCollapsed = !collapsedState[categoryId];
    const newState = { ...collapsedState, [categoryId]: isCollapsed };
    setCollapsedState(newState);
    setStoredJSON('categoryCollapseState', newState);
  };

  // Members of a composite service render nested under it, not as siblings.
  const topLevel = endpoints.filter(ep => !ep.parent_id);
  const membersByParent = endpoints.reduce((acc, ep) => {
    if (!ep.parent_id) return acc;
    if (!acc[ep.parent_id]) acc[ep.parent_id] = [];
    acc[ep.parent_id].push(ep);
    return acc;
  }, {});

  const getCategoryStatus = (categoryId) => {
    const categoryEndpoints = topLevel.filter(ep => ep.group_id === categoryId);
    if (categoryEndpoints.length === 0) return 'unknown';
    const statuses = categoryEndpoints.map(getDisplayStatus);
    if (statuses.includes('down')) return 'down';
    if (statuses.includes('degraded')) return 'degraded';
    if (statuses.includes('maintenance')) return 'maintenance';
    return 'up';
  };

  const getCategoryStatusIcon = (status) => {
    switch (status) {
      case 'up':       return <CheckCircle size={13} className="cat-status-icon cat-status-icon--up" />;
      case 'degraded': return <AlertCircle size={13} className="cat-status-icon cat-status-icon--degraded" />;
      case 'down':     return <ZapOff      size={13} className="cat-status-icon cat-status-icon--down" />;
      case 'maintenance': return <Wrench size={13} className="cat-status-icon cat-status-icon--maintenance" />;
      default:         return <AlertCircle size={13} className="cat-status-icon cat-status-icon--unknown" />;
    }
  };

  const renderServiceRow = (endpoint, isMember = false) => {
    const status = getDisplayStatus(endpoint);
    const uptime = formatUptimePercent(endpoint.uptime_30d, 1);
    const members = membersByParent[endpoint.id] || [];

    const row = (
      <div
        key={endpoint.id}
        className={`svc-row svc-row--${status} ${isMember ? 'svc-row--member' : ''}`}
        onClick={() => navigate(`/endpoint/${endpoint.id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && navigate(`/endpoint/${endpoint.id}`)}
      >
        <div className={`svc-row__accent svc-row__accent--${status}`} />

        <div className="svc-row__main">
          <div className={`svc-row__left ${endpoint.usage ? 'svc-row__left--usage' : ''}`}>
            <span className="svc-row__name">{endpoint.name}</span>
            <span className="svc-row__url">
              {endpoint.type === 'composite'
                ? `${members.length} component${members.length === 1 ? '' : 's'}`
                : `${endpoint.url}${endpoint.port ? `:${endpoint.port}` : ''}`}
            </span>
            <UsageMeter usage={endpoint.usage} compact />
          </div>

          <div className="svc-row__right">
            {endpoint.latest?.response_time != null && (
              <span className="svc-row__rt">{endpoint.latest.response_time}ms</span>
            )}
            {uptime && (
              <span className="svc-row__uptime" style={{ color: getUptimeColorValue(endpoint.uptime_30d) }}>
                {uptime} <span className="svc-row__uptime-period">30d</span>
              </span>
            )}
            <span className={`svc-row__badge svc-row__badge--${status}`}>
              {getStatusLabel(status)}
            </span>
            <ExternalLink size={12} className="svc-row__link-icon" />
          </div>
        </div>
      </div>
    );

    if (members.length === 0) return row;

    return (
      <div key={endpoint.id} className="svc-group">
        {row}
        <div className="svc-members">
          {members.map(member => renderServiceRow(member, true))}
        </div>
      </div>
    );
  };

  const renderCategory = (categoryId, name, categoryEndpoints, isUncategorized = false) => {
    const isCollapsed = collapsedState[categoryId];
    const status = isUncategorized ? 'unknown' : getCategoryStatus(categoryId);
    const allUp = categoryEndpoints.every(ep => ep.latest?.status === 'up');

    return (
      <div key={categoryId} className="cat-section">
        <button
          className="cat-header"
          onClick={() => toggleCategory(categoryId)}
          aria-expanded={!isCollapsed}
        >
          <div className="cat-header__left">
            {isCollapsed ? <ChevronRight size={15} className="cat-chevron" /> : <ChevronDown size={15} className="cat-chevron" />}
            {!isUncategorized && getCategoryStatusIcon(status)}
            <span className="cat-header__name">{name}</span>
            <span className="cat-header__count">{categoryEndpoints.length}</span>
          </div>
          <div className="cat-header__right">
            {!isCollapsed && allUp && (
              <span className="cat-all-up">All Operational</span>
            )}
          </div>
        </button>

        {!isCollapsed && (
          <div className="cat-body">
            {categoryEndpoints.map(ep => renderServiceRow(ep))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="categories-container">
      {categories && categories.length > 0 &&
        categories.map(category => {
          const categoryEndpoints = topLevel.filter(ep => ep.group_id === category.id);
          if (categoryEndpoints.length === 0) return null;
          return renderCategory(category.id, category.name, categoryEndpoints);
        })
      }

      {(() => {
        const uncategorized = topLevel.filter(ep => !ep.group_id);
        if (uncategorized.length === 0) return null;
        return renderCategory('uncategorized', 'Other Services', uncategorized, true);
      })()}
    </div>
  );
}

export default StatusPageCategories;
